import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import Context from "./Context";
import { useMediaQuery } from "@mui/material";
import {
  setIsAuthMenuOpen,
  setIsMiniMode,
  setIsMenuOpen,
  setIsMobileMenuOpen,
  setIsMiniSwitchVisibility,
} from "./store/actions";
import reducer from "./store/reducer";
import { MENU } from "types/menu";

const Provider = ({ appConfig, children, persistKey = "menu" }) => {
  const { menu } = appConfig || {};
  const {
    initialAuthMenuOpen,
    initialMiniMode,
    initialMenuOpen,
    initialMobileMenuOpen,
    initialMiniSwitchVisibility,
    useWindowWatcher,
  } = menu;

  const savedState = JSON.parse(localStorage.getItem(persistKey) || "null");

  const [menuStore, dispatch] = useReducer(reducer, {
    isAuthMenuOpen: initialAuthMenuOpen,
    isMiniMode: initialMiniMode,
    isMenuOpen: initialMenuOpen,
    isMobileMenuOpen: initialMobileMenuOpen,
    isMiniSwitchVisibility: initialMiniSwitchVisibility,
    ...(savedState ?? {}),
  });

  const setAuthMenuOpen = useCallback(
    (open) => dispatch(setIsAuthMenuOpen(open)),
    [],
  );
  const setMiniMode = useCallback((mini) => dispatch(setIsMiniMode(mini)), []);
  const setMenuOpen = useCallback((open) => dispatch(setIsMenuOpen(open)), []);
  const setMobileMenuOpen = useCallback(
    (open) => dispatch(setIsMobileMenuOpen(open)),
    [],
  );
  const setMiniSwitchVisibility = useCallback(
    (visible) => dispatch(setIsMiniSwitchVisibility(visible)),
    [],
  );

  const openMenu = useCallback(() => setMenuOpen(true), [setMenuOpen]);
  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
  const toggleMenuOpen = useCallback(
    () => setMenuOpen(!menuStore.isMenuOpen),
    [menuStore.isMenuOpen, setMenuOpen],
  );

  const collapseMenu = useCallback(() => setMiniMode(true), [setMiniMode]);
  const expandMenu = useCallback(() => setMiniMode(false), [setMiniMode]);
  const toggleMiniMode = useCallback(
    () => setMiniMode(!menuStore.isMiniMode),
    [menuStore.isMiniMode, setMiniMode],
  );

  const toggleThis = useCallback(
    (key, newValue = null) => {
      if (key === MENU.AUTH) {
        setAuthMenuOpen(
          newValue !== null ? newValue : !menuStore.isAuthMenuOpen,
        );
      } else if (key === MENU.MINI) {
        setMiniMode(newValue !== null ? newValue : !menuStore.isMiniMode);
      } else if (key === MENU.OPEN) {
        setMenuOpen(newValue !== null ? newValue : !menuStore.isMenuOpen);
      } else if (key === MENU.MOBILE_OPEN) {
        setMobileMenuOpen(
          newValue !== null ? newValue : !menuStore.isMobileMenuOpen,
        );
      } else if (key === MENU.MINI_SWITCH_VISIBLE) {
        setMiniSwitchVisibility(
          newValue !== null ? newValue : !menuStore.isMiniSwitchVisibility,
        );
      }
    },
    [
      menuStore.isAuthMenuOpen,
      menuStore.isMiniMode,
      menuStore.isMenuOpen,
      menuStore.isMobileMenuOpen,
      menuStore.isMiniSwitchVisibility,
      setAuthMenuOpen,
      setMiniMode,
      setMenuOpen,
      setMobileMenuOpen,
      setMiniSwitchVisibility,
    ],
  );

  const isDesktop = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    try {
      localStorage.setItem(persistKey, JSON.stringify(menuStore));
    } catch (error) {
      console.warn(error);
    }
  }, [menuStore, persistKey]);

  useEffect(() => {
    if (useWindowWatcher && !isDesktop) {
      setMenuOpen(false);
      setMiniMode(false);
    }
  }, [isDesktop, useWindowWatcher, setMenuOpen, setMiniMode]);

  const value = useMemo(
    () => ({
      isDesktop,

      // getters
      isAuthMenuOpen: menuStore.isAuthMenuOpen,
      isMiniMode: menuStore.isMiniMode,
      isMenuOpen: menuStore.isMenuOpen,
      isMobileMenuOpen: menuStore.isMobileMenuOpen,
      isMiniSwitchVisibility: menuStore.isMiniSwitchVisibility,

      // setters
      setAuthMenuOpen,
      setMiniMode,
      setMenuOpen,
      setMobileMenuOpen,
      setMiniSwitchVisibility,

      // helpers
      openMenu,
      closeMenu,
      toggleMenuOpen,
      collapseMenu,
      expandMenu,
      toggleMiniMode,
      toggleThis,
    }),
    [
      isDesktop,
      menuStore.isAuthMenuOpen,
      menuStore.isMiniMode,
      menuStore.isMenuOpen,
      menuStore.isMobileMenuOpen,
      menuStore.isMiniSwitchVisibility,
      setAuthMenuOpen,
      setMiniMode,
      setMenuOpen,
      setMobileMenuOpen,
      setMiniSwitchVisibility,
      openMenu,
      closeMenu,
      toggleMenuOpen,
      collapseMenu,
      expandMenu,
      toggleMiniMode,
      toggleThis,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default Provider;
